import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Send, Bot, User, Loader2, Lightbulb, CreditCard, TrendingUp, Home, Briefcase } from 'lucide-react';

import { API } from '@/config/api';

const topics = [
  { id: 'credit', label: 'Credit Building', icon: CreditCard, prompt: 'How can I improve my credit score?' },
  { id: 'investing', label: 'Investing', icon: TrendingUp, prompt: 'What are the basics of stock market investing?' },
  { id: 'realestate', label: 'Real Estate', icon: Home, prompt: 'How do I get started with real estate investing?' },
  { id: 'business', label: 'Business', icon: Briefcase, prompt: 'What should I know about starting a business?' },
];

export default function FinancialConsulting() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Add welcome message on mount
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: `Welcome to the Nouveau Riche Legacy Financial Advisory! I'm your AI-powered financial advisor, here to help you build generational wealth.\n\nI can assist you with:\n• Personal finance and budgeting\n• Credit building and repair strategies\n• Stock market investing fundamentals\n• Cryptocurrency and blockchain basics\n• Real estate investment principles\n• Business and entrepreneurship guidance\n\nHow can I help you on your wealth-building journey today?`
    }]);
  }, []);
  
  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;
    
    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: messageText,
          session_id: sessionId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to get response');
      }
      
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get response. Please try again.');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error. Please try again or refresh the page.' 
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };
  
  const handleTopicClick = (topic) => {
    sendMessage(topic.prompt);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  return (
    <div className="min-h-screen bg-[#1c1814] pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="financial-consulting">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl sm:text-4xl text-[#FAFAFA] mb-2">
            AI Financial <span className="text-[#D4AF37]">Advisor</span>
          </h1>
          <p className="font-body text-[#FAFAFA]/60">
            Get personalized financial guidance powered by advanced AI.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Topics Sidebar */}
          <div className="lg:col-span-1">
            <Card className="card-finance" data-testid="topics-card">
              <CardHeader className="border-b border-[#D4AF37]/20 pb-4">
                <CardTitle className="font-display text-lg text-[#FAFAFA] flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-[#D4AF37]" />
                  Quick Topics
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-2">
                {topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => handleTopicClick(topic)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#D4AF37]/10 border border-transparent hover:border-[#D4AF37]/30 transition-all"
                    data-testid={`topic-${topic.id}`}
                  >
                    <topic.icon className="h-5 w-5 text-[#D4AF37]" />
                    <span className="text-[#FAFAFA] text-sm font-body">{topic.label}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          </div>
          
          {/* Chat Interface */}
          <div className="lg:col-span-3">
            <Card className="card-finance h-[calc(100vh-250px)] flex flex-col" data-testid="chat-card">
              {/* Messages */}
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-6">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${index}`}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
                          <Bot className="h-5 w-5 text-[#D4AF37]" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] p-4 ${
                          message.role === 'user'
                            ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/30'
                            : 'bg-[#252540] border border-[#FAFAFA]/10'
                        }`}
                      >
                        <p className="text-[#FAFAFA] font-body text-sm whitespace-pre-wrap">
                          {message.content}
                        </p>
                      </div>
                      {message.role === 'user' && (
                        <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
                          <User className="h-5 w-5 text-[#1c1814]" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-4 justify-start">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center">
                        <Bot className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div className="bg-[#252540] border border-[#FAFAFA]/10 p-4">
                        <Loader2 className="h-5 w-5 text-[#D4AF37] animate-spin" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              
              {/* Input */}
              <div className="border-t border-[#D4AF37]/20 p-4">
                <div className="flex gap-4">
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about financial advice, credit, investing..."
                    className="flex-1 bg-[#252540] border-[#D4AF37]/30 text-[#FAFAFA] placeholder:text-[#FAFAFA]/40 focus:border-[#D4AF37]"
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading}
                    className="btn-gold px-6"
                    data-testid="send-button"
                  >
                    <Send className="h-5 w-5" />
                  </Button>
                </div>
                <p className="text-xs text-[#FAFAFA]/40 mt-2 text-center">
                  AI-powered advice is for educational purposes. Always consult licensed professionals.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
